import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {summarizeConversation} from '../../../../helpers/draftAssistantHelpers';
import {HttpResponseError} from '../../../../services/http/httpError';
import {DialogButtonGroup} from '../../../common/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../common/dialogs/dialogContent';
import {DraftAssistantLoader} from '../../composer/toolbar/dropdowns/draftAssistantLoader';
import {CommentComposerIcon} from './commentComposerIcon';

/*
 * Props.
 */

interface CommentComposerSummarizeProps {
  conversationId: number;
  isDisabled?: boolean;
  onInsertCommentComposerText: HandlerOf<string>;
  companyUrl: string;
}

/*
 * Constants.
 */

const dropdownWidth = 320;

/*
 * Styles.
 */

const StyledDescriptionDiv = styled.div`
  padding-bottom: 12px;
`;

/*
 * Component.
 */

export const CommentComposerSummarize: FC<CommentComposerSummarizeProps> = (props) => {
  const {companyUrl, conversationId, isDisabled, onInsertCommentComposerText} = props;

  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADED);
  const [error, setError] = useState<HttpResponseError | undefined>(undefined);

  const onClickSave = async (onRequestClose: Handler) => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    const summary = await summarizeConversation({
      conversationId,
      companyUrl,
    }).catch((err) => {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      if (err instanceof HttpResponseError) {
        setError(err);
      }
    });

    // Something went wrong - set loading state to failed so user can retry.
    if (!summary) {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      return;
    }

    onInsertCommentComposerText(summary);
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    setError(undefined);

    if (onRequestClose) {
      onRequestClose();
    }
  };

  const renderDropdownContent = (popoverProps: PopoverTransferProps) => {
    // Loading and error states
    if (loadingStatus === AsyncStatusesEnum.LOADING || loadingStatus === AsyncStatusesEnum.FAILED) {
      return (
        <DraftAssistantLoader
          error={error}
          loadingStatus={loadingStatus}
          loadingMessage={
            <Trans id="draft-assistant-autocompose-async-loading">Generating conversation summary...</Trans>
          }
          onRetryClick={() => onClickSave(popoverProps.onRequestClose)}
        />
      );
    }

    return (
      <>
        <DialogContent>
          <StyledDescriptionDiv>
            <Trans id="comment-composer-summarize-tip">
              A short summary of this conversation will be generated using AI.
            </Trans>
          </StyledDescriptionDiv>
        </DialogContent>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={popoverProps.onRequestClose}>
            <Trans id="dropdown-cancel">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => onClickSave(popoverProps.onRequestClose)}
          >
            <Trans id="comment-composer-generate-summary">Generate</Trans>
          </Button>
        </DialogButtonGroup>
      </>
    );
  };

  const renderButton: RendererOf<boolean> = (isOpen) => (
    <CommentComposerIcon iconName="magicWand" isDisabled={isDisabled} hasHover={isOpen} />
  );

  const renderDropdown: RendererOf<PopoverTransferProps> = (popoverProps) => (
    <Dropdown {...popoverProps} width={dropdownWidth} placement="top-end">
      <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Summarize with AI</Trans>} />
      {renderDropdownContent(popoverProps)}
    </Dropdown>
  );

  return (
    <PopoverCoordinator
      shouldPreventMouseDown
      isDisabled={isDisabled}
      renderButton={renderButton}
      renderModal={renderDropdown}
    />
  );
};
